import { Box, Button, Typography } from '@mui/material';
import { Reader } from '@stripe/terminal-js';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripeTerminal } from 'src/hooks/useStripeTerminal';

import { Link } from './Link';
import { TextField } from './TextField';

interface PaymentMethod {
  id: string;
}

interface SwipeCardProps {
  model: {
    swipeCard: boolean;
    paymentMethodId: string | null;
  };
  onModelUpdate: (key: string, value: any) => void;
}

export const SwipeCard: FC<SwipeCardProps> = ({ model, onModelUpdate }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'swipeCard' });
  const [swipeText, setSwipeText] = useState<string>(t('startCardSwipe'));
  const [cardSwiping, setCardSwiping] = useState<boolean>(false);
  const [currentSelectedReader, setCurrentSelectedReader] =
    useState<string>('');
  const successfulText = t('swipeSuccessful');

  const terminal = useStripeTerminal();

  const swipeSuccessful = swipeText === successfulText;

  const cardSwiped = (paymentMethod: PaymentMethod) => {
    onModelUpdate('paymentMethodId', paymentMethod.id);
    setSwipeText(successfulText);
  };

  const toggleSwipeCard = () => {
    const value = !model.swipeCard;
    onModelUpdate('swipeCard', value);
    onModelUpdate('paymentMethodId', null);
    setCardSwiping(false);
    setSwipeText(t('startCardSwipe'));
    setCurrentSelectedReader('');
    terminal.cancelReadReusableCard();
  };

  const handleSwipeCard = () => {
    setSwipeText(t('waitingForSwipe'));
    setCardSwiping(true);
    terminal.saveCardForLater((pm: PaymentMethod) => cardSwiped(pm));
  };

  const cancelSwipe = () => {
    terminal.cancelReadReusableCard();
    setCardSwiping(false);
    setSwipeText(t('startCardSwipe'));
  };

  const retryReader = () => {
    cancelSwipe();
    terminal.retryReader();
  };

  const selectReaderHandler = (readerId: string) => {
    setCurrentSelectedReader(readerId);
    const reader = terminal.connectedReaders.find(
      (reader) => reader.id === readerId
    );
    if (reader) {
      terminal.connectReader(reader);
    }
  };

  if (!terminal.connectedReaders.length) {
    return null;
  }

  return (
    <>
      {model.swipeCard ? (
        <Box>
          {terminal.selectedReader ? (
            <>
              <Typography fontWeight="bold" component="span">
                {t('cardReader')}:
              </Typography>{' '}
              {terminal.selectedReader?.label}
              <Box my={2} />
              {terminal.connecting ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography>{t('loading')}</Typography>
                </Box>
              ) : (
                <>
                  <Box>
                    <Button
                      onClick={handleSwipeCard}
                      disabled={cardSwiping}
                      variant="prominent"
                      color="primary"
                      sx={{ mr: 1 }}
                    >
                      {swipeText}
                    </Button>
                    {cardSwiping && !swipeSuccessful && (
                      <Button onClick={cancelSwipe} variant="outlined">
                        {t('cancelSwipe')}
                      </Button>
                    )}
                  </Box>

                  {!swipeSuccessful && (
                    <Box mt={2}>
                      <Button onClick={retryReader} variant="outlined">
                        {t('resetConnection')}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          ) : (
            <TextField
              fullWidth
              value={currentSelectedReader}
              onChange={(e) => selectReaderHandler(e.target.value as string)}
              options={[
                { value: '', label: t('selectReader') },
                ...terminal.connectedReaders
                  .filter((reader: Reader) => reader.status === 'online')
                  .map((reader: Reader) => ({
                    value: reader.id,
                    label: reader.label,
                  })),
              ]}
            />
          )}

          <Button component={Link} onClick={toggleSwipeCard} color="primary">
            {t('removeCard')}
          </Button>
        </Box>
      ) : (
        <Box>
          <Button component={Link} onClick={toggleSwipeCard} color="primary">
            {t('addPaymentMethod')}
          </Button>
        </Box>
      )}
    </>
  );
};
